import {
  type DocumentTypeKey,
  type ProcessorCapability,
  type ProcessorRolloutState,
} from "./primitives.js"
import type { ProcessorVersionMetadata } from "./processor/types.js"
import type { Check } from "./analysis/checks.js"

export type DocumentTypeRegistryDefinition = {
  readonly key: DocumentTypeKey
  readonly name: string
  readonly description: string
  readonly version: string
  readonly summaryFields: readonly string[]
  readonly recordTypes: readonly string[]
  readonly exportTemplates: readonly string[]
  readonly postingCapabilities: readonly string[]
  /**
   * Declarative extraction checks, the single source for both the worker
   * (extraction time) and the API recheck (after every review edit). The
   * bank statement's running-balance chain is code, not config, and lives
   * alongside evaluateChecks in analysis/.
   */
  readonly checks?: readonly Check[]
  /**
   * Projection column -> summary field. The worker builds the projection row
   * from this at extraction; the API mirrors an edited summary field back
   * into its typed projection column through the same map, so list views
   * follow summary fixes.
   */
  readonly projection?: Readonly<Record<string, string>>
  readonly schemaHash: string
}

export const documentTypeRegistryDefinitions = [
  {
    key: "bank_statement",
    name: "Bank Statement",
    description:
      "Bank and credit account statements with account-period summaries and transaction rows.",
    version: "1",
    summaryFields: [
      "financial_institution",
      "account_name",
      "account_number_last4",
      "period_start",
      "period_end",
      "opening_balance",
      "closing_balance",
      "total_deposits",
      "total_withdrawals",
      "currency",
      "transaction_count",
      "transaction_total",
      "calculated_closing_balance",
      "reconciliation_difference",
      "reconciliation_status",
    ],
    recordTypes: ["transaction"],
    exportTemplates: [
      "ledgerbox_transactions_csv",
      "ledgerbox_transactions_xlsx",
      "quickbooks_csv_3_column",
      "quickbooks_csv_4_column",
      "quickbooks_web_connect_qbo",
      // Both want dated, signed transactions against one account, so both are
      // offered here and not on the document types that have neither. An
      // invoice has no statement period to declare and no balance to carry.
      "ofx_bank_statement",
      "qif_bank_statement",
    ],
    postingCapabilities: ["transaction_import"],
    // The independent direction check: when the statement prints deposit and
    // withdrawal totals, the sign split must reproduce them to the cent. This
    // is what catches a flipped sign on statements with no balance column.
    checks: [
      {
        rowSum: {
          rowsOfType: "transaction",
          rowField: "amount",
          signFilter: "positive",
          label: "deposits",
        },
        equals: "total_deposits",
        tolerance: "0.01",
      },
      {
        rowSum: {
          rowsOfType: "transaction",
          rowField: "amount",
          signFilter: "negative",
          absolute: true,
          label: "withdrawals",
        },
        equals: "total_withdrawals",
        tolerance: "0.01",
      },
    ],
    projection: {
      counterpartyName: "financial_institution",
      accountName: "account_name",
      accountNumberLast4: "account_number_last4",
      periodStart: "period_start",
      periodEnd: "period_end",
      total: "closing_balance",
      currency: "currency",
    },
    schemaHash:
      "sha256:c16d4bd99a2f48bcd54d19b3d80d5b04d74661dc17e648937134afeee57d8682",
  },
  {
    key: "invoice",
    name: "Invoice",
    description:
      "Vendor invoices with due dates, totals, tax, counterparty details, and line items.",
    version: "1",
    summaryFields: [
      "vendor_name",
      "invoice_number",
      "invoice_date",
      "due_date",
      "subtotal",
      "tax_total",
      "total",
      "currency",
    ],
    recordTypes: ["line_item"],
    exportTemplates: ["csv_line_items"],
    postingCapabilities: ["bill", "expense"],
    checks: [
      {
        required: [
          "vendor_name",
          "invoice_number",
          "invoice_date",
          "total",
          "currency",
        ],
      },
      { sum: ["subtotal", "tax_total"], equals: "total", tolerance: "0.01" },
    ],
    projection: {
      counterpartyName: "vendor_name",
      periodStart: "invoice_date",
      periodEnd: "due_date",
      total: "total",
      currency: "currency",
    },
    schemaHash:
      "sha256:241e5154243054cf07588ce94b495130710ae2acbbbb3498f23942ab8570c279",
  },
  {
    key: "receipt",
    name: "Receipt",
    description:
      "Point-of-sale and expense receipts with merchant, payment, total, and line item details.",
    version: "1",
    summaryFields: [
      "merchant_name",
      "transaction_date",
      "payment_method",
      "subtotal",
      "tax_total",
      "tip_total",
      "total",
      "currency",
    ],
    recordTypes: ["line_item"],
    exportTemplates: ["csv_line_items"],
    postingCapabilities: ["expense"],
    checks: [
      { required: ["merchant_name", "transaction_date", "total", "currency"] },
      {
        sum: ["subtotal", "tax_total", "tip_total"],
        equals: "total",
        tolerance: "0.01",
      },
    ],
    projection: {
      counterpartyName: "merchant_name",
      periodStart: "transaction_date",
      periodEnd: "transaction_date",
      total: "total",
      currency: "currency",
    },
    schemaHash:
      "sha256:3a474c727c295f657f2d98fc13e9793a4a75f39440b9607b61d808e74ddcb1ab",
  },
  {
    key: "general_table",
    name: "General Table",
    description:
      "Tabular source documents that do not fit a specialized financial document type yet.",
    version: "1",
    summaryFields: ["title", "page_count", "row_count", "column_count"],
    recordTypes: ["table_row"],
    exportTemplates: ["csv_table"],
    postingCapabilities: [],
    projection: {},
    schemaHash:
      "sha256:814c33dafb81bc97269213d8ba1c7d1cd77f7fbba732ffb77e96cce20c98b9d7",
  },
  {
    key: "pay_stub",
    name: "Pay Stub",
    description:
      "Payroll pay stubs with employer, employee, pay period, earnings, and deduction lines.",
    version: "1",
    summaryFields: [
      "employer_name",
      "employee_name",
      "pay_period_start",
      "pay_period_end",
      "pay_date",
      "gross_pay",
      "total_deductions",
      "net_pay",
      "currency",
    ],
    recordTypes: ["earning", "deduction"],
    exportTemplates: ["csv_line_items"],
    postingCapabilities: [],
    checks: [
      {
        required: [
          "employer_name",
          "employee_name",
          "pay_date",
          "gross_pay",
          "net_pay",
          "currency",
        ],
      },
      {
        reconcile: {
          value: "gross_pay",
          minusRowsOfType: "deduction",
          rowField: "amount",
          equals: "net_pay",
        },
        tolerance: "0.01",
      },
    ],
    projection: {
      counterpartyName: "employer_name",
      accountName: "employee_name",
      periodStart: "pay_period_start",
      periodEnd: "pay_period_end",
      total: "net_pay",
      currency: "currency",
    },
    schemaHash:
      "sha256:abe2f3f118ffb96795183f76d3c8affc989448c12ac0c651f3a07c263d6d14d8",
  },
  {
    key: "purchase_order",
    name: "Purchase Order",
    description:
      "Buyer-issued purchase orders with supplier, delivery, totals, and ordered line item details.",
    version: "1",
    summaryFields: [
      "buyer_name",
      "vendor_name",
      "purchase_order_number",
      "order_date",
      "expected_delivery_date",
      "ship_to_address",
      "subtotal",
      "tax_total",
      "shipping_total",
      "total",
      "currency",
    ],
    recordTypes: ["line_item"],
    exportTemplates: ["csv_line_items"],
    postingCapabilities: [],
    checks: [
      {
        required: [
          "buyer_name",
          "vendor_name",
          "purchase_order_number",
          "order_date",
          "total",
          "currency",
        ],
      },
      {
        sum: ["subtotal", "tax_total", "shipping_total"],
        equals: "total",
        tolerance: "0.01",
      },
    ],
    projection: {
      counterpartyName: "vendor_name",
      accountName: "buyer_name",
      periodStart: "order_date",
      periodEnd: "expected_delivery_date",
      total: "total",
      currency: "currency",
    },
    schemaHash:
      "sha256:e04af4321b1094af53e7059dddda17fe25158652a7b7ad61951779e993e3a89e",
  },
  {
    key: "brokerage_statement",
    name: "Brokerage Statement",
    description:
      "Investment and brokerage account statements with holdings, account activity, and realized gain and loss rows.",
    version: "1",
    summaryFields: [
      "custodian_name",
      "account_name",
      "account_number_last4",
      "period_start",
      "period_end",
      "beginning_value",
      "ending_value",
      "currency",
    ],
    recordTypes: ["holding", "activity", "realized_gain"],
    exportTemplates: ["csv_line_items", "realized_gains_csv"],
    postingCapabilities: [],
    checks: [
      {
        required: [
          "custodian_name",
          "period_start",
          "period_end",
          "ending_value",
          "currency",
        ],
      },
      // Holdings should account for the ending value; soft because statements
      // legitimately drift (accrued interest, pending activity, rounding).
      {
        rowSum: { rowsOfType: "holding", rowField: "market_value" },
        equals: "ending_value",
        tolerance: "0.02",
        tolerancePercent: "0.1",
        soft: true,
      },
      // Every realized-gain lot must reconcile to the cent; hard.
      {
        rowReconcile: {
          rowsOfType: "realized_gain",
          minuend: "proceeds",
          subtrahend: "cost_basis",
          equals: "gain_loss",
        },
        tolerance: "0.01",
      },
    ],
    projection: {
      counterpartyName: "custodian_name",
      accountName: "account_name",
      periodStart: "period_start",
      periodEnd: "period_end",
      total: "ending_value",
      currency: "currency",
    },
    schemaHash:
      "sha256:6ea37a82466be6fe4c032967cdf0082927bcaf373a18642fd38274caa350a27f",
  },
  {
    key: "w2",
    name: "W-2",
    description:
      "IRS Form W-2 wage and tax statements with employer, employee, and per-box wage and withholding amounts.",
    version: "1",
    summaryFields: [
      "tax_year",
      "employer_name",
      "employer_ein",
      "employee_name",
      "employee_ssn_last4",
      "wages",
      "federal_tax_withheld",
      "social_security_wages",
      "social_security_tax_withheld",
      "medicare_wages",
      "medicare_tax_withheld",
      "state",
      "state_wages",
      "state_tax_withheld",
    ],
    recordTypes: [],
    exportTemplates: ["csv_summary"],
    postingCapabilities: [],
    checks: [
      { required: ["tax_year", "employer_name", "employee_name", "wages"] },
      // Box 4 should be 6.2% of box 3, within half a percent. Soft: the wage
      // base cap, corrections, and rounding edge cases are real, so a mismatch
      // flags the form for review rather than blocking approval.
      {
        sum: ["social_security_wages"],
        coefficient: "0.062",
        equals: "social_security_tax_withheld",
        tolerance: "0.01",
        tolerancePercent: "0.5",
        soft: true,
      },
    ],
    projection: {
      counterpartyName: "employer_name",
      accountName: "employee_name",
      total: "wages",
    },
    schemaHash:
      "sha256:f69bd6eaf606c9e918c235366c531fb1039209a3dc798606c2d56c9be99d917b",
  },
  {
    key: "form_1099",
    name: "Form 1099",
    description:
      "IRS Form 1099 information returns (NEC, MISC, INT, DIV, K, B) with payer, recipient, and reported box amounts.",
    version: "1",
    summaryFields: [
      "tax_year",
      "form_variant",
      "payer_name",
      "payer_tin",
      "recipient_name",
      "recipient_tin_last4",
      "federal_tax_withheld",
      "state",
      "state_tax_withheld",
    ],
    recordTypes: ["reported_amount"],
    exportTemplates: ["csv_summary", "csv_line_items"],
    postingCapabilities: [],
    checks: [
      {
        required: ["tax_year", "form_variant", "payer_name", "recipient_name"],
      },
    ],
    projection: {
      counterpartyName: "payer_name",
      accountName: "recipient_name",
    },
    schemaHash:
      "sha256:2ec1f36bb73bf2596267b77ecdbd8bb262a66a5c66b0a27ecccc5a0a2bad01cb",
  },
  {
    key: "check_image",
    name: "Check",
    description:
      "Scanned or photographed checks with payee, numeric and written amounts, date, memo, and bank routing details.",
    version: "1",
    summaryFields: [
      "check_number",
      "check_date",
      "payee_name",
      "amount",
      "amount_in_words",
      "memo",
      "bank_name",
      "account_number_last4",
      "routing_number",
    ],
    recordTypes: [],
    exportTemplates: ["csv_summary"],
    postingCapabilities: [],
    checks: [
      { required: ["check_date", "payee_name", "amount"] },
      // The whole reason the type exists: the legal line, parsed to a number by
      // the model (amount_from_words), must agree with the courtesy-box amount.
      // Hard: a mismatch raises amount_mismatch and blocks approval. The
      // tolerance rounds to zero cents, so agreement means exact cent equality.
      { sum: ["amount_from_words"], equals: "amount", tolerance: "0.001" },
    ],
    projection: {
      counterpartyName: "payee_name",
      accountName: "bank_name",
      total: "amount",
    },
    schemaHash:
      "sha256:a5752c3b3e503565e852d216038e008a0a9a943e3f046b45ae1d832225296751",
  },
  {
    key: "merchant_statement",
    name: "Merchant statement",
    // The description doubles as the auto-detect classifier prompt, so it has
    // to discriminate against the one genuinely confusable type: a bank
    // statement from the same business, same month, same shape of table.
    description:
      "A card processing statement from a payment processor (Square, Stripe, Clover, Toast, Fiserv) showing gross card volume, transaction counts, and an itemized list of processing fees for a billing period. Not a bank statement: the counterparty is a payment processor and the line items are fees charged, not account transactions.",
    version: "v1",
    summaryFields: [
      "processor_key",
      "processor_name",
      "period_start",
      "period_end",
      "gross_volume",
      "transaction_count",
      "total_fees",
      "effective_rate",
      "interchange_total",
      "assessments_total",
      "markup_total",
      "other_fees_total",
      "fee_taxonomy_version",
    ],
    recordTypes: ["fee_line", "card_volume"],
    exportTemplates: ["csv_rows", "csv_summary"],
    // Nothing to post: a fee statement is not a ledger source in v1.
    postingCapabilities: [],
    checks: [
      {
        required: ["period_start", "period_end", "gross_volume", "total_fees"],
      },
      // The reconciliation gate: extracted fee lines must sum to the printed
      // fee total. Hard, because this one check is the accuracy measure, the
      // fabricated-statement filter, and the public index's admission gate.
      {
        rowSum: { rowsOfType: "fee_line", rowField: "amount" },
        equals: "total_fees",
        tolerance: "0.01",
      },
      // Card-mix volumes should sum to gross volume; soft because processors
      // report mix inconsistently (some exclude keyed or ACH volume).
      {
        rowSum: { rowsOfType: "card_volume", rowField: "volume" },
        equals: "gross_volume",
        tolerance: "0.02",
        tolerancePercent: "0.5",
        soft: true,
      },
    ],
    projection: {
      counterpartyName: "processor_name",
      periodStart: "period_start",
      periodEnd: "period_end",
      total: "total_fees",
    },
    schemaHash:
      "sha256:40d0f3641381aa87c5a8a3e7d0197c7e4dc1b1f27ea01cc48ba611c787fc1bdb",
  },
] as const satisfies readonly DocumentTypeRegistryDefinition[]

export type ProcessorVersionRegistryDefinition = {
  readonly key: string
  readonly version: string
  readonly provider: string
  readonly runtime: string
  readonly externalModelId: string | null
  readonly externalAnalyzerId: string | null
  readonly configHash: string
  readonly promptHash: string | null
  readonly schemaHash: string
  readonly capabilities: readonly ProcessorCapability[]
  readonly supportedDocumentTypeKeys: readonly DocumentTypeKey[]
  readonly rolloutState: ProcessorRolloutState
  readonly metadata: ProcessorVersionMetadata
}

export type ProcessorRegistryDefinition = {
  readonly key: string
  readonly name: string
  readonly description: string
  readonly versions: readonly ProcessorVersionRegistryDefinition[]
}

export const processorRegistryDefinitions = [
  {
    key: "foundry_llm_extraction",
    name: "Foundry LLM Extraction",
    description:
      "Per-page multimodal LLM extraction on Azure AI Foundry with deterministic merge and balance verification.",
    versions: [
      {
        // The prompt/schema/config fingerprints are computed from
        // apps/workers/ocr/src/schemas/bank-statement.ts; a worker unit test
        // asserts these literals stay in sync. Deployment names are runtime
        // config (EXTRACTION_MODEL_DEPLOYMENT) — externalModelId records the
        // default model family for provenance, not a binding.
        key: "foundry_llm_extraction",
        version: "1",
        provider: "azure",
        runtime: "foundry_llm_extraction",
        externalModelId: "gpt-5.6-luna",
        externalAnalyzerId: null,
        configHash:
          "sha256:bd20e4de7b0aa5bd89012e3433823aee93562496886a6c2e2ee34c550c6bf831",
        promptHash:
          "sha256:199c82e9d5ca75613c57b6be0a99d18b0d9860a69a8d43bb3ceb55ffba58d57c",
        schemaHash:
          "sha256:396d91a5bd4a83d8a1c5f60d09763258ec91ace7de8a4c09ed245b8e86fc583f",
        capabilities: [
          "extraction",
          "normalization",
          "validation",
          "csv_export",
        ],
        supportedDocumentTypeKeys: [
          "bank_statement",
          "invoice",
          "receipt",
          "general_table",
          "pay_stub",
          "purchase_order",
          "brokerage_statement",
          "w2",
          "form_1099",
          "check_image",
          "merchant_statement",
        ],
        rolloutState: "active",
        metadata: {
          modelId: "gpt-5.6-luna",
          escalationModelId: "gpt-5.6-terra",
          apiVersion: "2024-10-21",
          storesSecrets: false,
        },
      },
    ],
  },
] as const satisfies readonly ProcessorRegistryDefinition[]

/**
 * The processor version key every new processing job is stamped with — the
 * single extraction pipeline. Derived from the registry so a key rename
 * cannot drift from what the API stamps and the worker accepts.
 */
export const activeProcessorVersionKey =
  processorRegistryDefinitions[0].versions[0].key

/**
 * The document types the active processor version can extract, and therefore
 * the only types selectable on upload. Derived from the processor's
 * supportedDocumentTypeKeys so a type becomes selectable exactly when its
 * extractor ships (and the DB compatibility gate agrees). Never empty:
 * bank_statement is always supported.
 */
const [firstSelectableDocumentTypeKey, ...remainingSelectableDocumentTypeKeys] =
  processorRegistryDefinitions[0].versions[0].supportedDocumentTypeKeys

if (!firstSelectableDocumentTypeKey) {
  throw new Error("The active processor version must support a document type")
}

export const selectableDocumentTypeKeys: readonly [
  DocumentTypeKey,
  ...DocumentTypeKey[],
] = [firstSelectableDocumentTypeKey, ...remainingSelectableDocumentTypeKeys]

/**
 * The registry definitions for the selectable types, in registry order. The
 * type selector on review and the worker's classifier both need the label +
 * description for each selectable type; derive it once here.
 */
export const selectableDocumentTypeDefinitions =
  documentTypeRegistryDefinitions.filter((definition) =>
    selectableDocumentTypeKeys.includes(definition.key)
  )

/**
 * The declarative checks for a document type — the one lookup both the
 * worker's extraction pass and the API's review-time recheck go through.
 */
export const documentTypeChecks = (key: DocumentTypeKey): readonly Check[] => {
  const definition: DocumentTypeRegistryDefinition | undefined =
    documentTypeRegistryDefinitions.find((entry) => entry.key === key)
  return definition?.checks ?? []
}

/**
 * The projection column -> summary field map for a document type, shared by
 * the worker's projection builder and the API's summary-patch mirroring.
 */
export const documentTypeProjection = (
  key: DocumentTypeKey
): Readonly<Record<string, string>> => {
  const definition: DocumentTypeRegistryDefinition | undefined =
    documentTypeRegistryDefinitions.find((entry) => entry.key === key)
  return definition?.projection ?? {}
}
